import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import { useSnackbar } from 'notistack';
import Modal from '../../../../ui/canon/Modal';
import Button from '../../../../ui/canon/Button';
import Input from '../../../../ui/canon/Input';

const UPDATE_VAULT_DOCUMENT = gql`
  mutation UpdateVaultDocument(
    $id: ID!
    $title: String
    $description: String
    $category: String
    $tags: [String!]
    $shared_with_negotiating: Boolean
  ) {
    update_vault_document(
      id: $id
      title: $title
      description: $description
      category: $category
      tags: $tags
      shared_with_negotiating: $shared_with_negotiating
    ) {
      id
      title
      description
      category
      tags
      shared_with_negotiating
    }
  }
`;

const DocumentEditModal = ({
  open,
  document,
  onClose,
  onSuccess,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('other');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [sharedWithNegotiating, setSharedWithNegotiating] = useState(false);

  useEffect(() => {
    if (document) {
      setTitle(document.title || '');
      setDescription(document.description || '');
      setCategory(document.category || 'other');
      setTags(document.tags || []);
      setSharedWithNegotiating(document.shared_with_negotiating || false);
    }
  }, [document]);

  const categories = [
    { value: 'other', label: intl.formatMessage({ id: 'succession.vault.cat_other' }) },
    { value: 'financial', label: intl.formatMessage({ id: 'succession.vault.cat_financial' }) },
    { value: 'legal', label: intl.formatMessage({ id: 'succession.vault.cat_legal' }) },
    { value: 'operational', label: intl.formatMessage({ id: 'succession.vault.cat_operational' }) },
    { value: 'marketing', label: intl.formatMessage({ id: 'succession.vault.cat_marketing' }) },
    { value: 'hr', label: intl.formatMessage({ id: 'succession.vault.cat_hr' }) },
  ];

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSubmit = (updateMutation) => {
    if (!title.trim()) {
      enqueueSnackbar(intl.formatMessage({ id: 'succession.vault.doc_title_required' }), { variant: 'warning' });
      return;
    }

    updateMutation({
      variables: {
        id: document.id,
        title: title.trim(),
        description: description.trim() || null,
        category,
        tags,
        shared_with_negotiating: sharedWithNegotiating,
      },
    }).then(() => {
      enqueueSnackbar(intl.formatMessage({ id: 'succession.vault.edit_updated' }), { variant: 'success' });
      onSuccess();
    }).catch(error => {
      enqueueSnackbar(intl.formatMessage({ id: 'succession.vault.edit_error' }, { error: error.message }), { variant: 'error' });
    });
  };

  if (!document) return null;

  return (
    <Mutation mutation={UPDATE_VAULT_DOCUMENT}>
      {(updateDocument) => (
        <Modal
          open={open}
          onClose={onClose}
          className="bg-succession-bg-primary text-succession-text-primary rounded-lg shadow-succession-lg border border-succession-border w-full max-w-2xl animate-modal-scale-in"
        >
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-succession-text-primary">
                {intl.formatMessage({ id: 'succession.vault.edit_title' })}
              </h2>
              <button
                onClick={onClose}
                className="p-1 text-succession-text-secondary hover:text-succession-text-primary hover:bg-succession-hover-bg rounded-md transition-theme duration-theme"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-succession-bg-secondary p-4 rounded-lg mb-4">
                <p className="text-sm text-succession-text-primary mb-1">
                  <strong>{intl.formatMessage({ id: 'succession.vault.detail_file_label' })}</strong>{' '}
                  {document.file_name || document.document?.name || intl.formatMessage({ id: 'succession.vault.unknown' })}
                </p>
                <p className="text-xs text-succession-text-secondary">
                  <strong>{intl.formatMessage({ id: 'succession.vault.detail_source_label' })}</strong>{' '}
                  {document.source_type?.replace(/_/g, ' ') || intl.formatMessage({ id: 'succession.vault.unknown' })} •{' '}
                  {intl.formatMessage({ id: 'succession.vault.detail_uploaded_by_label' }, { name: document.uploaded_by_name || intl.formatMessage({ id: 'succession.vault.unknown' }) })}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-1">
                  {intl.formatMessage({ id: 'succession.vault.doc_title_label' })} <span className="text-red-500">*</span>
                </label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={intl.formatMessage({ id: 'succession.vault.doc_title_placeholder' })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-1">
                  {intl.formatMessage({ id: 'succession.vault.doc_description_label' })}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted border border-succession-border hover:border-succession-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring rounded-md transition-theme duration-theme text-sm px-3 py-1.5 resize-none"
                  rows={3}
                  placeholder={intl.formatMessage({ id: 'succession.vault.doc_description_placeholder' })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-1">
                  {intl.formatMessage({ id: 'succession.vault.doc_category_label' })}
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full bg-succession-bg-primary text-succession-text-primary border border-succession-border hover:border-succession-border-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-succession-focus-ring rounded-md transition-theme duration-theme text-sm px-3 py-1.5 appearance-none pr-8"
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-1">
                  {intl.formatMessage({ id: 'succession.vault.doc_tags_label' })}
                </label>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    placeholder={intl.formatMessage({ id: 'succession.vault.doc_tags_placeholder' })}
                    className="flex-1"
                  />
                  <Button variant="outline" size="md" onClick={handleAddTag}>
                    {intl.formatMessage({ id: 'succession.vault.action_add' })}
                  </Button>
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-0.5 text-xs bg-succession-bg-secondary text-succession-text-secondary rounded-md flex items-center gap-1"
                      >
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          className="hover:text-succession-text-primary"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-4 border border-succession-border rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer">
                  <div className="relative inline-block w-10 h-5">
                    <input
                      type="checkbox"
                      checked={sharedWithNegotiating}
                      onChange={(e) => setSharedWithNegotiating(e.target.checked)}
                      className="sr-only"
                    />
                    <div
                      className={`absolute inset-0 rounded-full transition-all duration-200 ${
                        sharedWithNegotiating
                          ? 'bg-succession-primary'
                          : 'bg-succession-bg-secondary'
                      }`}
                    />
                    <div
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200 ${
                        sharedWithNegotiating ? 'translate-x-5' : ''
                      }`}
                    />
                  </div>
                  <span className="text-sm font-medium text-succession-text-primary">
                    {intl.formatMessage({ id: 'succession.vault.doc_sharing_label' })}
                  </span>
                </label>
                <p className="text-xs text-succession-text-secondary mt-1 ml-12">
                  {intl.formatMessage({ id: 'succession.vault.doc_sharing_desc' })}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-succession-border">
              <Button variant="outline" onClick={onClose}>
                {intl.formatMessage({ id: 'succession.vault.action_cancel' })}
              </Button>
              <Button
                variant="primary"
                onClick={() => handleSubmit(updateDocument)}
                disabled={!title.trim()}
              >
                {intl.formatMessage({ id: 'succession.vault.action_save' })}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Mutation>
  );
};

DocumentEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  document: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default DocumentEditModal;
